import React from 'react';
import { useDispatch } from 'react-redux';
import { Formik, FormikActions, FormikProps } from 'formik';

import { useSnackbarContext } from 'ui-lib/components/Snackbar/SnackbarProvider';
import {
  authSignIn,
  authSignOut,
  initializePendoUser,
} from 'web-shared/actions/auth';
import { getUserCompanies } from 'web-shared/actions/user-company';
import { fetchUserPreferences } from 'web-shared/modules/UserPreferences/actions';
import { getPortalLevelPermissions } from 'web-shared/modules/Permissions/api';
import { SharedDispatch } from 'web-shared/reducers/types';
import HttpError from 'web-shared/services/http-service/HttpError';

import {
  initialValues,
  SignInFormValues,
  validationSchema,
} from '../../const';

interface Props {
  children: (props: FormikProps<SignInFormValues>) => React.ReactNode;
  onAuthEnd: () => void;
}

const AuthPageFormikWrapper = ({ children, onAuthEnd }: Props) => {
  const { showErrorSnackbar } = useSnackbarContext();
  const dispatch = useDispatch<SharedDispatch>();

  const handleSubmit = async (
    values: SignInFormValues,
    { setErrors, setSubmitting }: FormikActions<SignInFormValues>,
  ) => {
    setSubmitting(true);

    try {
      await dispatch(authSignIn(values));
    } catch (error) {
      setErrors({
        email: '',
        password: error instanceof Error ? error.message : '',
      });
      setSubmitting(false);
      return;
    }

    try {
      await dispatch(getUserCompanies());
      await getPortalLevelPermissions({ params: undefined });
    } catch (error) {
      if (HttpError.isHttpError(error) || error instanceof Error) {
        showErrorSnackbar({ message: error.message });
      }
      setSubmitting(false);
      return;
    }

    try {
      await dispatch(fetchUserPreferences());
      dispatch(initializePendoUser());
      onAuthEnd();
    } catch (error) {
      if (HttpError.isHttpError(error) || error instanceof Error) {
        showErrorSnackbar({ message: error.message });
      }
      dispatch(authSignOut());
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
    >
      {children}
    </Formik>
  );
};

export default AuthPageFormikWrapper;
